import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { Trophy } from 'lucide-react-native';
import { FLAGS } from '@/lib/featureFlags';

export default function CreateChallengeScreen() {
  const { gymId } = useLocalSearchParams<{ gymId: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { theme } = useTheme();

  // V1: Challenges disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_CHALLENGES_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reward, setReward] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isOwner, setIsOwner] = useState<boolean | null>(null);

  useEffect(() => {
    checkOwnership();
  }, [gymId]);

  const checkOwnership = async () => {
    if (!gymId) return;

    try {
      const { data } = await supabase
        .from('gym_memberships')
        .select('role')
        .eq('gym_id', gymId)
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .maybeSingle();

      setIsOwner(data?.role === 'owner');
    } catch {
      setIsOwner(false);
    }
  };

  const handleSubmit = async () => {
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!startDate || !endDate) {
      setError('Start and end dates are required');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setError('Invalid date format. Use YYYY-MM-DD');
      return;
    }

    if (end < start) {
      setError('End date must be after start date');
      return;
    }

    setLoading(true);

    try {
      const { error: insertError } = await supabase.from('gym_challenges').insert({
        gym_id: gymId,
        created_by: user!.id,
        title: title.trim(),
        description: description.trim() || null,
        reward: reward.trim() || null,
        start_date: startDate,
        end_date: endDate,
        is_active: true,
      });

      if (insertError) throw insertError;

      Alert.alert('Created', 'Challenge Set', [
        {
          text: 'OK',
          onPress: () => router.push(`/(gym)/${gymId}`),
        },
      ]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isOwner === null) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (isOwner === false) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
          <View style={{ width: 24 }} />
          <Text style={[styles.headerTitle, { color: theme.colors.text }]}>CREATE CHALLENGE</Text>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.errorContainer}>
          <Trophy size={48} color={theme.colors.textTertiary} />
          <Text style={[styles.errorTitle, { color: theme.colors.text }]}>Owner Only</Text>
          <Text style={[styles.errorSubtitle, { color: theme.colors.textSecondary }]}>
            Only gym owners can create challenges
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ width: 24 }} />
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>CREATE CHALLENGE</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {error ? <Text style={[styles.errorMessage, { color: theme.colors.error, backgroundColor: theme.colors.surface, borderColor: theme.colors.error }]}>{error}</Text> : null}

          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Title *</Text>
            <TextInput
              style={[
                styles.textInput,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder="Challenge title"
              placeholderTextColor={theme.colors.textTertiary}
              value={title}
              onChangeText={setTitle}
              editable={!loading}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Description</Text>
            <TextInput
              style={[
                styles.textArea,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder="What is this challenge about?"
              placeholderTextColor={theme.colors.textTertiary}
              value={description}
              onChangeText={setDescription}
              editable={!loading}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Reward</Text>
            <TextInput
              style={[
                styles.textInput,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder="Prize or recognition"
              placeholderTextColor={theme.colors.textTertiary}
              value={reward}
              onChangeText={setReward}
              editable={!loading}
            />
          </View>

          <View style={styles.row}>
            <View style={[styles.inputContainer, styles.flex]}>
              <Text style={[styles.label, { color: theme.colors.text }]}>Start Date *</Text>
              <TextInput
                style={[
                  styles.textInput,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={theme.colors.textTertiary}
                value={startDate}
                onChangeText={setStartDate}
                editable={!loading}
              />
            </View>

            <View style={[styles.inputContainer, styles.flex]}>
              <Text style={[styles.label, { color: theme.colors.text }]}>End Date *</Text>
              <TextInput
                style={[
                  styles.textInput,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={theme.colors.textTertiary}
                value={endDate}
                onChangeText={setEndDate}
                editable={!loading}
              />
            </View>
          </View>

          <TouchableOpacity
            style={[
              styles.submitButton,
              { backgroundColor: theme.colors.accent },
              loading && styles.submitButtonDisabled,
            ]}
            onPress={handleSubmit}
            disabled={loading}>
            {loading ? (
              <ActivityIndicator color={theme.colors.text} />
            ) : (
              <Text style={[styles.submitButtonText, { color: theme.colors.text }]}>
                Create Challenge
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1.5,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },
  content: {
    gap: 24,
  },
  inputContainer: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  textInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 16,
    fontSize: 16,
    height: 56,
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 16,
    fontSize: 16,
    minHeight: 120,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  flex: {
    flex: 1,
  },
  submitButton: {
    paddingVertical: 18,
    borderRadius: 2,
    alignItems: 'center',
    marginTop: 8,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 18,
    fontWeight: '700',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 20,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '900',
  },
  errorSubtitle: {
    fontSize: 16,
    textAlign: 'center',
  },
});
